/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext } from 'react';
import { object, func } from 'prop-types';
import { Controller } from 'react-hook-form';

// MUI
import {
    TextField, Select, InputLabel, FormControl, FormHelperText, Grid
} from '@material-ui/core';
import TextFieldHelper from '../../Helpers/TextFieldHelper';
import { emailTypes, phoneNumberTypes, modifiedRelationships } from '../../Helpers/Helpers';
import { RecipientBookContext } from '../../Context/RecipientBookContext';
import { TypeChecking } from '../../Assets/TypeChecking';

const AddProfileForm = ({ methods, recipientProfile, setRecipientFirstInitial, setRecipientLastInitial }) => {
    const { recipientState } = useContext(RecipientBookContext);
    const {
        register, formState: { errors }, setValue, control, clearErrors,
    } = methods;

    const relationships = modifiedRelationships({ recipientProfile, recipientState });

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
                {/* First Name */}
                <FormControl variant="filled" fullWidth sx={{ m: 1 }}>
                    <TextField
                        id="rcpFirstName"
                        type="text"
                        variant="filled"
                        fullWidth
                        required
                        inputProps={{
                            autoComplete: 'new-password'
                        }}
                        label="First Name"
                        error={errors?.rcpFirstName?.message}
                        defaultValue={recipientProfile?.rcpFirstName || ''}
                        onChange={(e) => {
                            setRecipientFirstInitial(e.target.value[0] || '')
                            setValue('rcpFirstName', e.target.value)
                        }}
                        {...register('rcpFirstName')}
                    />
                    {errors?.rcpFirstName?.message && (
                        <FormHelperText>{errors?.rcpFirstName?.message}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
                {/* Last Name */}
                <FormControl variant="filled" fullWidth sx={{ m: 1 }}>
                    <TextField
                        id="rcpLastName"
                        type="text"
                        variant="filled"
                        fullWidth
                        required
                        inputProps={{
                            autoComplete: 'new-password'
                        }}
                        label="Last Name"
                        error={errors?.rcpLastName?.message}
                        defaultValue={recipientProfile?.rcpLastName}
                        onChange={(e) => {
                            setRecipientLastInitial(e.target.value[0] || '')
                            setValue('rcpLastName', e.target.value)
                        }}
                        {...register('rcpLastName')}
                    />
                    {errors?.rcpLastName?.message && (
                        <FormHelperText>{errors?.rcpLastName?.message}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
                {/* Relationship */}
                <FormControl variant="filled" fullWidth sx={{ m: 1 }}>
                    <InputLabel id="relationship-label">Relationship</InputLabel>
                    <Controller
                        name="relationship"
                        control={control}
                        defaultValue={recipientProfile?.relationship || ''}
                        render={({ field }) => (
                            <Select
                                {...field}
                                labelId="relationship-label"
                                id="relationship"
                                variant="filled"
                                label="Relationship"
                                error={errors?.relationship?.message}
                                native
                                {...register('relationship')}
                            >   
                                <option key="" value=""></option>
                                {Object.values(relationships).map((relationship) => (
                                    <option key={relationship} value={relationship}>{relationship}</option>
                                ))}
                            </Select>
                        )}
                    />
                    <FormHelperText>{errors?.relationship?.message || ''}</FormHelperText>
                </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
                {/* Employer Name */}
                <FormControl variant="filled" fullWidth sx={{ m: 1 }}>
                    <TextField
                        id="rcpEmployerName"
                        type="text"
                        variant="filled"
                        fullWidth
                        inputProps={{
                            autoComplete: 'new-password'
                        }}
                        label="Company Name"
                        error={errors?.rcpEmployerName?.message}
                        defaultValue={recipientProfile?.rcpEmployerName} // need antony to pass this from the top level
                        onInput={(e) => {
                            const sanitizedValue = TypeChecking.AlphaNumericSpecial({ value: e.target.value });
                            setValue('rcpEmployerName', sanitizedValue);
                        }}
                        {...register('rcpEmployerName')}
                    />
                    {errors?.rcpEmployerName?.message && (
                        <FormHelperText>{errors?.rcpEmployerName?.message}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
                <Grid container spacing={2}>
                    <Grid item xs={12} lg={8} style={{ paddingBottom: '12px' }}>
                        {/* Email */}
                        <FormControl variant="filled" fullWidth sx={{ m: 1 }}>
                            <TextField
                                type="text"
                                variant="filled"
                                fullWidth
                                label="Email Address"
                                error={errors?.rcpEmail?.message}
                                defaultValue={recipientProfile?.rcpEmail || ''}
                                onChange={({ target }) => {
                                    setValue('rcpEmail', target.value)
                                }}
                                {...register('rcpEmail')}
                            />
                            {errors?.rcpEmail?.message && (
                                <FormHelperText>{errors?.rcpEmail?.message}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        {/* Email */}
                        <FormControl variant="filled" fullWidth sx={{ m: 1 }}>
                            <InputLabel id="relationship-label" style={{ fontWeight: 700 }}>Email Type</InputLabel>
                            <Controller
                                name="rcpEmailType"
                                control={control}
                                defaultValue={recipientProfile?.rcpEmailType}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        labelId="relationship-label"
                                        id="rcpEmailType"
                                        variant="filled"
                                        label="Email Type"
                                        error={errors?.relationship?.message}
                                        native
                                        {...register('rcpEmailType')}
                                    >
                                        {emailTypes.map((emailType) => (
                                            <option key={emailType} value={emailType?.toLowerCase()}>{emailType}</option>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{errors?.rcpEmailType?.message || ''}</FormHelperText>
                        </FormControl>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} lg={6}>
                <Grid container spacing={2}>
                    <Grid item xs={12} lg={8} style={{ paddingBottom: '12px' }}>
                        {/* Phone Number */}
                        <FormControl variant="filled" fullWidth sx={{ m: 1 }}>
                            <TextFieldHelper
                                style={{}}
                                required={false}
                                methods={methods}
                                optionsList={false}
                                fieldName="rcpPhone"
                                fieldLabel="Phone Number"
                                address={{
                                    rcpPhone: recipientProfile?.rcpPhone,
                                    rcpPhoneType: recipientProfile?.rcpPhoneType,
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        {/* Phone Number */}
                        <FormControl variant="filled" fullWidth sx={{ m: 1 }}>
                            <InputLabel id="relationship-label" style={{ fontWeight: 700 }}>Phone Type</InputLabel>
                            <Controller
                                name="rcpPhoneType"
                                control={control}
                                defaultValue={recipientProfile?.rcpPhoneType}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        labelId="relationship-label"
                                        id="rcpPhoneType"
                                        variant="filled"
                                        label="Phone Number Type"
                                        error={errors?.relationship?.message}
                                        native
                                        {...register('rcpPhoneType', { onChange: () => { clearErrors('rcpPhone'); setValue('rcpPhone', '') } })}
                                    >
                                        {phoneNumberTypes.map((phoneNumberType) => (
                                            <option key={phoneNumberType} value={phoneNumberType?.toLowerCase()}>{phoneNumberType}</option>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{errors?.rcpPhoneType?.message || ''}</FormHelperText>
                        </FormControl>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

AddProfileForm.propTypes = {
    recipientProfile: object,
    methods: object.isRequired,
    setRecipientLastInitial: func.isRequired,
    setRecipientFirstInitial: func.isRequired,
};

export default AddProfileForm;
